// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/PcpOrdemForm.tsx
//  PCP — Formulário / Detalhe de Ordem de Produção
// ════════════════════════════════════════════════════════════════════════════

import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, Factory, PlayCircle,
  CheckCircle2, PauseCircle, XCircle,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { pcpService }          from '@/features/operacional/services/pcpService';
import { etapaProducaoService } from '@/features/operacional/services/etapaProducaoService';
import { calcularStatusPcp }   from '@/features/operacional/types';
import { getApiErrorMessage }  from '@/lib/apiClient';

import { Button }              from '@/components/ui/button';
import { Input }               from '@/components/ui/input';
import { Label }               from '@/components/ui/label';
import { Textarea }            from '@/components/ui/textarea';
import { Badge }               from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator }           from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { ProdutoSearchCombobox }  from '@/components/ProdutoSearchCombobox';
import { PessoaSearchCombobox }   from '@/components/PessoaSearchCombobox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

import type {
  CriarPcpOrdemRequest,
  PcpOrdemItemRequest,
  AtualizarPcpOrdemRequest,
  PcpOrdemDetalhe,
} from '@/features/operacional/types';

// ── NovaOrdemForm ─────────────────────────────────────────────────────────

interface SugestaoState {
  produtoId:  number;
  descricao:  string;
  quantidade: number;
  referencia: string;
}

function NovaOrdemForm() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const location    = useLocation();
  const sugestao    = (location.state as { sugestao?: SugestaoState } | null)?.sugestao;

  const hoje = new Date().toISOString().slice(0, 10);
  const [dtEmissao,          setDtEmissao]          = useState(hoje);
  const [dtPrevisaoInicio,   setDtPrevisaoInicio]   = useState(hoje);
  const [dtPrevisaoTermino,  setDtPrevisaoTermino]  = useState(hoje);
  const [dtPrevisaoEntrega,  setDtPrevisaoEntrega]  = useState(hoje);
  const [referencia,         setReferencia]         = useState(sugestao?.referencia ?? '');
  const [observacao,         setObservacao]         = useState('');
  const [isPedidoInterno,    setIsPedidoInterno]    = useState(true);
  const [clienteId,          setClienteId]          = useState<number | undefined>(undefined);
  const [itens, setItens]   = useState<(PcpOrdemItemRequest & { _key: number })[]>(
    sugestao
      ? [{ _key: 1, produtoId: sugestao.produtoId, quantidadePrevista: sugestao.quantidade, custoUnitarioPrevisto: 0 }]
      : []
  );
  const [_nextKey, setNextKey] = useState(sugestao ? 2 : 1);

  const { data: etapas = [] } = useQuery({
    queryKey: ['etapas-producao'],
    queryFn: () => etapaProducaoService.getAll(false),
  });

  const addItem = () => setNextKey((k) => { setItens((p) => [...p, { _key: k, produtoId: 0, quantidadePrevista: 1, custoUnitarioPrevisto: 0, etapaId: 0 }]); return k + 1; });
  const removeItem = (key: number) => setItens((p) => p.filter((r) => r._key !== key));
  const updateItemNum = (key: number, field: string, value: string) =>
    setItens((p) => p.map((r) => r._key === key ? { ...r, [field]: Number(value) } : r));
  const setItemProduto = (key: number, produtoId: number) =>
    setItens((p) => p.map((r) => r._key === key ? { ...r, produtoId } : r));

  const saveMutation = useMutation({
    mutationFn: () => {
      if (itens.length === 0) throw new Error('Adicione ao menos um item');
      const request: CriarPcpOrdemRequest = {
        dtEmissao:         new Date(dtEmissao).toISOString(),
        dtPrevisaoInicio:  new Date(dtPrevisaoInicio).toISOString(),
        dtPrevisaoTermino: new Date(dtPrevisaoTermino).toISOString(),
        dtPrevisaoEntrega: new Date(dtPrevisaoEntrega).toISOString(),
        itens: itens.map(({ produtoId, quantidadePrevista, custoUnitarioPrevisto, etapaId }) => ({
          produtoId, quantidadePrevista, custoUnitarioPrevisto, etapaId: etapaId || 0,
        })),
        referencia:      referencia || undefined,
        observacao:      observacao || undefined,
        isPedidoInterno,
        clienteId:       !isPedidoInterno ? clienteId : undefined,
      };
      return pcpService.create(request);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pcp'] });
      toast.success('Ordem de Produção criada com sucesso');
      navigate(`/operacional/pcp/${data.id}`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const custoPrevisto = itens.reduce((s, i) => s + i.quantidadePrevista * (i.custoUnitarioPrevisto ?? 0), 0);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/pcp')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">Nova Ordem de Produção</h1>
      </div>

      <Tabs defaultValue="identificacao" className="space-y-4">
        <TabsList>
          <TabsTrigger value="identificacao">Identificação</TabsTrigger>
          <TabsTrigger value="itens">Itens</TabsTrigger>
          <TabsTrigger value="observacoes">Observações</TabsTrigger>
        </TabsList>

        <TabsContent value="identificacao">
          <Card>
            <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data de Emissão</Label>
                <Input type="date" value={dtEmissao} onChange={(e) => setDtEmissao(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Referência</Label>
                <Input value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="Código / referência da ordem" />
              </div>
              <div className="space-y-2">
                <Label>Previsão Início</Label>
                <Input type="date" value={dtPrevisaoInicio} onChange={(e) => setDtPrevisaoInicio(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Previsão Término</Label>
                <Input type="date" value={dtPrevisaoTermino} onChange={(e) => setDtPrevisaoTermino(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Previsão Entrega</Label>
                <Input type="date" value={dtPrevisaoEntrega} onChange={(e) => setDtPrevisaoEntrega(e.target.value)} />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <input
                  id="isPedidoInterno"
                  type="checkbox"
                  checked={isPedidoInterno}
                  onChange={(e) => { setIsPedidoInterno(e.target.checked); if (e.target.checked) setClienteId(undefined); }}
                  className="h-4 w-4 rounded border border-input"
                />
                <Label htmlFor="isPedidoInterno" className="cursor-pointer">Pedido Interno</Label>
              </div>
              {!isPedidoInterno && (
                <div className="space-y-2 md:col-span-2">
                  <Label>Cliente</Label>
                  <PessoaSearchCombobox
                    value={clienteId}
                    onChange={setClienteId}
                    filter="cliente"
                    placeholder="Selecione o cliente..."
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="itens">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Itens / Produtos</CardTitle>
                <Button size="sm" variant="outline" className="gap-2" onClick={addItem}>
                  <Plus className="h-4 w-4" />Adicionar Item
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {itens.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Factory className="h-10 w-10 mx-auto mb-2 opacity-30" />
                  <p>Nenhum item adicionado</p>
                  <Button variant="outline" className="mt-3 gap-2" onClick={addItem}>
                    <Plus className="h-4 w-4" />Adicionar item
                  </Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[220px]">Produto</TableHead>
                      <TableHead className="min-w-[150px]">Etapa</TableHead>
                      <TableHead className="text-right">Qtd. Prevista</TableHead>
                      <TableHead className="text-right">Custo Unit. Previsto</TableHead>
                      <TableHead className="text-right">Custo Total</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itens.map((i) => (
                      <TableRow key={i._key}>
                        <TableCell>
                          <ProdutoSearchCombobox
                            value={i.produtoId || undefined}
                            onChange={(id) => setItemProduto(i._key, id)}
                            placeholder="Buscar produto..."
                          />
                        </TableCell>
                        <TableCell>
                          <select
                            className="h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
                            value={i.etapaId ?? 0}
                            onChange={(e) => updateItemNum(i._key, 'etapaId', e.target.value)}>
                            <option value={0}>— sem etapa —</option>
                            {etapas.map((et) => (
                              <option key={et.id} value={et.id}>{et.descricao}</option>
                            ))}
                          </select>
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" value={i.quantidadePrevista} className="w-24 text-right"
                            onChange={(e) => updateItemNum(i._key, 'quantidadePrevista', e.target.value)} />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input type="number" value={i.custoUnitarioPrevisto ?? 0} className="w-28 text-right"
                            onChange={(e) => updateItemNum(i._key, 'custoUnitarioPrevisto', e.target.value)} />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {(i.quantidadePrevista * (i.custoUnitarioPrevisto ?? 0))
                            .toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" onClick={() => removeItem(i._key)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={4} className="text-right font-medium text-muted-foreground">Custo Total Previsto:</TableCell>
                      <TableCell className="text-right font-bold">
                        {custoPrevisto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="observacoes">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                <Label>Observações</Label>
                <Textarea value={observacao} onChange={(e) => setObservacao(e.target.value)}
                  placeholder="Observações sobre a ordem de produção..." rows={6} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate('/operacional/pcp')}>Cancelar</Button>
        <Button className="gap-2" onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4" />Criar Ordem
        </Button>
      </div>
    </div>
  );
}

// ── OrdemDetalheView ──────────────────────────────────────────────────────

function OrdemDetalheView() {
  const navigate    = useNavigate();
  const { id }      = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data: ordem, isLoading } = useQuery({
    queryKey: ['pcp-detalhe', id],
    queryFn: () => pcpService.getById(Number(id)),
    enabled: !!id,
  });

  const { data: etapas = [] } = useQuery({
    queryKey: ['etapas-producao'],
    queryFn: () => etapaProducaoService.getAll(true),
  });
  const etapaNome = (etapaId: number) =>
    etapas.find((e) => e.id === etapaId)?.descricao ?? (etapaId ? `#${etapaId}` : '—');

  const updateMutation = useMutation({
    mutationFn: (data: AtualizarPcpOrdemRequest) => pcpService.update(Number(id), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pcp-detalhe', id] });
      queryClient.invalidateQueries({ queryKey: ['pcp'] });
      toast.success('Ordem atualizada');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  // ── Conclusão (apontamento + baixa de estoque) ──────────────────────────
  const [concluirOpen, setConcluirOpen] = useState(false);
  const [qtdProduzidas, setQtdProduzidas] = useState<Record<number, number>>({});

  const concluirMutation = useMutation({
    mutationFn: (data: PcpOrdemDetalhe) =>
      pcpService.concluir(Number(id), {
        itens: data.itens.map((it) => ({
          ordemItemId: it.id,
          quantidadeProduzida: qtdProduzidas[it.id] ?? it.quantidadePrevista,
        })),
      }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['pcp-detalhe', id] });
      queryClient.invalidateQueries({ queryKey: ['pcp'] });
      setConcluirOpen(false);
      toast.success(
        `Produção concluída — ${res.quantidadeProduzida.toLocaleString('pt-BR')} un. produzidas, ${res.insumosBaixados} insumo(s) baixado(s) do estoque.`,
      );
    },
    onError: (error) => {
      // 409 = já finalizada (outra aba/sessão concluiu). Recarrega para refletir o estado real.
      queryClient.invalidateQueries({ queryKey: ['pcp-detalhe', id] });
      setConcluirOpen(false);
      toast.error(getApiErrorMessage(error));
    },
  });

  const abrirConcluir = (o: PcpOrdemDetalhe) => {
    setQtdProduzidas(
      Object.fromEntries(o.itens.map((it) => [it.id, it.quantidadeProduzida || it.quantidadePrevista])),
    );
    setConcluirOpen(true);
  };

  if (isLoading) return <div className="p-6"><div className="text-center py-12 text-muted-foreground">Carregando...</div></div>;
  if (!ordem) return null;

  const status = calcularStatusPcp(ordem);

  const statusBadgeClass: Record<string, string> = {
    aberta:       'bg-blue-500/10 text-blue-500',
    em_andamento: 'bg-orange-500/10 text-orange-500',
    pausada:      'bg-yellow-500/10 text-yellow-600',
    finalizada:   'bg-green-500/10 text-green-500',
    cancelada:    'bg-red-500/10 text-red-500',
  };

  const statusLabel: Record<string, string> = {
    aberta: 'Aberta', em_andamento: 'Em Andamento', pausada: 'Pausada',
    finalizada: 'Finalizada', cancelada: 'Cancelada',
  };

  return (
    <div className="p-6 space-y-4">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/operacional/pcp')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Ordem #{ordem.numeroOrdem}</h1>
            {ordem.referencia && <p className="text-muted-foreground text-sm mt-0.5">{ordem.referencia}</p>}
          </div>
          <Badge className={statusBadgeClass[status]}>{statusLabel[status]}</Badge>
        </div>
        <div className="flex gap-2 flex-wrap">
          {status === 'aberta' && (
            <Button variant="outline" className="gap-2"
              onClick={() => updateMutation.mutate({ isIniciado: true, dtInicio: new Date().toISOString() })}>
              <PlayCircle className="h-4 w-4" />Iniciar Produção
            </Button>
          )}
          {status === 'em_andamento' && (
            <Button variant="outline" className="gap-2"
              onClick={() => updateMutation.mutate({ isParalizado: true })}>
              <PauseCircle className="h-4 w-4" />Pausar
            </Button>
          )}
          {status === 'pausada' && (
            <Button variant="outline" className="gap-2"
              onClick={() => updateMutation.mutate({ isParalizado: false })}>
              <PlayCircle className="h-4 w-4" />Retomar
            </Button>
          )}
          {(status === 'em_andamento' || status === 'pausada') && (
            <>
              <Button className="gap-2 bg-green-600 hover:bg-green-700"
                onClick={() => abrirConcluir(ordem)}
                disabled={updateMutation.isPending || concluirMutation.isPending}>
                <CheckCircle2 className="h-4 w-4" />Concluir / Apontar Produção
              </Button>
              <Button variant="outline" className="gap-2 text-destructive border-destructive hover:bg-destructive/10"
                onClick={() => updateMutation.mutate({ isCancelado: true })}>
                <XCircle className="h-4 w-4" />Cancelar
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Info */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-muted-foreground">Emissão</p>
              <p className="font-medium">{new Date(ordem.dtEmissao).toLocaleDateString('pt-BR')}</p></div>
            <div><p className="text-muted-foreground">Prev. Início</p>
              <p className="font-medium">{new Date(ordem.dtPrevisaoInicio).toLocaleDateString('pt-BR')}</p></div>
            <div><p className="text-muted-foreground">Prev. Entrega</p>
              <p className="font-medium">{new Date(ordem.dtPrevisaoEntrega).toLocaleDateString('pt-BR')}</p></div>
            <div><p className="text-muted-foreground">Início Real</p>
              <p className="font-medium">{ordem.dtInicio ? new Date(ordem.dtInicio).toLocaleDateString('pt-BR') : '—'}</p></div>
          </div>
          <Separator className="my-4" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><p className="text-muted-foreground">Custo Previsto</p>
              <p className="font-bold">{ordem.custoPrevisto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p></div>
            <div><p className="text-muted-foreground">Custo Efetivo</p>
              <p className="font-medium">{ordem.custoEfetivo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p></div>
            <div><p className="text-muted-foreground">Qtd. Prevista</p>
              <p className="font-medium">{ordem.quantidadePrevista}</p></div>
            <div><p className="text-muted-foreground">Qtd. Efetiva</p>
              <p className="font-medium">{ordem.quantidadeEfetiva}</p></div>
          </div>
          {ordem.observacao && (
            <>
              <Separator className="my-4" />
              <p className="text-sm text-muted-foreground">{ordem.observacao}</p>
            </>
          )}
        </CardContent>
      </Card>

      {/* Itens */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Factory className="h-5 w-5" />Itens da Ordem
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!ordem.itens || ordem.itens.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">Nenhum item nesta ordem</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Produto ID</TableHead>
                  <TableHead>Etapa</TableHead>
                  <TableHead className="text-right">Qtd. Prevista</TableHead>
                  <TableHead className="text-right">Qtd. Utilizada</TableHead>
                  <TableHead className="text-right">Qtd. Produzida</TableHead>
                  <TableHead className="text-right">Custo Prev.</TableHead>
                  <TableHead className="text-right">Custo Efetivo</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {ordem.itens.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.produtoId}</TableCell>
                    <TableCell>{etapaNome(item.etapaId)}</TableCell>
                    <TableCell className="text-right">{item.quantidadePrevista}</TableCell>
                    <TableCell className="text-right">{item.quantidadeUtilizada}</TableCell>
                    <TableCell className="text-right">{item.quantidadeProduzida}</TableCell>
                    <TableCell className="text-right">
                      {item.custoTotalPrevisto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.custoTotalEfetivo.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                    </TableCell>
                    <TableCell>
                      {item.isFinalizado
                        ? <Badge className="bg-green-500/10 text-green-500">Finalizado</Badge>
                        : item.isIniciado
                          ? <Badge className="bg-orange-500/10 text-orange-500">Em Produção</Badge>
                          : <Badge className="bg-blue-500/10 text-blue-500">Aguardando</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog de conclusão / apontamento */}
      <Dialog open={concluirOpen} onOpenChange={setConcluirOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Concluir Ordem #{ordem.numeroOrdem}</DialogTitle>
            <DialogDescription>
              Informe a quantidade efetivamente produzida de cada item. Ao concluir, o sistema
              dá <strong>entrada do produto acabado</strong> e <strong>baixa os insumos</strong> da
              ficha técnica no estoque. Esta ação é definitiva.
            </DialogDescription>
          </DialogHeader>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Produto ID</TableHead>
                <TableHead className="text-right">Qtd. Prevista</TableHead>
                <TableHead className="text-right">Qtd. Produzida</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ordem.itens.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-medium">{item.produtoId}</TableCell>
                  <TableCell className="text-right">{item.quantidadePrevista}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min={0}
                      step="any"
                      className="w-28 text-right ml-auto"
                      value={qtdProduzidas[item.id] ?? item.quantidadePrevista}
                      onChange={(e) =>
                        setQtdProduzidas((p) => ({ ...p, [item.id]: Number(e.target.value) }))
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <DialogFooter>
            <Button variant="outline" onClick={() => setConcluirOpen(false)}>Cancelar</Button>
            <Button
              className="gap-2 bg-green-600 hover:bg-green-700"
              onClick={() => concluirMutation.mutate(ordem)}
              disabled={concluirMutation.isPending}>
              <CheckCircle2 className="h-4 w-4" />
              {concluirMutation.isPending ? 'Concluindo...' : 'Confirmar Conclusão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Export ────────────────────────────────────────────────────────────────

const PcpOrdemForm = () => {
  const { id } = useParams<{ id: string }>();
  return id && id !== 'nova' ? <OrdemDetalheView /> : <NovaOrdemForm />;
};

export default PcpOrdemForm;
